import { useState, useEffect, useRef } from 'react';
import api from './api';
import { useFilePreview, FilePreviewModal } from './FilePreview';
import { flaggedDocuments } from './FreelancersPage';
import { FolderNode } from './FolderNode';

function fileIcon(name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return '🖼️';
  if (ext === 'pdf') return '📄';
  if (['doc', 'docx'].includes(ext)) return '📝';
  if (['xls', 'xlsx'].includes(ext)) return '📊';
  return '📎';
}

function Folder({ icon, name, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: '12px', marginBottom: '16px', overflow: 'hidden' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '16px 20px', cursor: 'pointer', userSelect: 'none' }}>
        <span style={{ fontSize: '18px' }}>{icon}</span>
        <span style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: '14px', letterSpacing: '0.5px' }}>{name}</span>
        <span style={{ marginLeft: 'auto', color: 'var(--text-dim)', fontSize: '12px' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && <div style={{ padding: '0 20px 18px' }}>{children}</div>}
    </div>
  );
}

// docId/onReplace/replacing — опційні, тільки для рядків, що відповідають
// реальному завантаженому документу (паспорт/фото/CV/інше). Коли документ
// позначений (flagReason), поруч з причиною зʼявляється кнопка "Replace" —
// відкриває системний файловий діалог і одразу вантажить нову версію замість
// того, щоб фрілансер сам ішов у Profile видаляти й перезавантажувати файл.
function FileRow({ href, downloadHref, name, hint, flagReason, docId, onReplace, replacing }) {
  const { previewFile, openPreview, closePreview } = useFilePreview();
  const fileInputRef = useRef(null);
  return (
    <div style={{ borderBottom: '1px solid var(--bg-hover)', padding: '9px 0' }}>
      <div
        onClick={() => openPreview({ url: href, downloadUrl: downloadHref || href, name })}
        style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--info-text)', cursor: 'pointer', fontSize: '13px' }}>
        <span>{fileIcon(name)}</span>
        <span>{name}</span>
        {hint && <span style={{ color: 'var(--text-dim)', fontSize: '11px', marginLeft: 'auto' }}>{hint}</span>}
        <FilePreviewModal file={previewFile} onClose={closePreview} />
      </div>
      {flagReason && (
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', marginTop: '5px', background: 'var(--danger-bg)', border: '1px solid var(--accent)', borderRadius: '6px', padding: '5px 10px' }}>
          <span style={{ fontSize: '12px' }}>🚩</span>
          <span style={{ color: 'var(--danger-text-soft)', fontSize: '11px' }}>Needs correction: {flagReason}</span>
          {docId && onReplace && (
            <>
              <button type="button" disabled={replacing} onClick={() => fileInputRef.current?.click()}
                style={{
                  marginLeft: 'auto', flexShrink: 0, background: 'var(--accent)', border: 'none', color: '#fff',
                  fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '6px',
                  cursor: replacing ? 'default' : 'pointer', opacity: replacing ? 0.6 : 1,
                }}>
                {replacing ? 'Uploading…' : '🔄 Replace'}
              </button>
              <input
                ref={fileInputRef} type="file" style={{ display: 'none' }}
                onChange={e => {
                  const file = e.target.files?.[0];
                  e.target.value = ''; // дозволяє вибрати той самий файл повторно
                  if (file) onReplace(docId, file);
                }}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Обгортка-заголовок на кожен проєкт у списку PROJECTS — суто UI-групування
// (не відповідає жодному вузлу docFolders), тому лишається статичною назвою
// проєкту зі своїм collapse; сам вміст (Project Documents / власна папка)
// рендериться через FolderNode — там і живе реальне керування папками.
function SubFolder({ name, hasContent, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginTop: '10px' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-body)', fontSize: '13px', fontWeight: 600, marginBottom: '4px', cursor: 'pointer', userSelect: 'none' }}>
        <span style={{ fontSize: '10px', color: 'var(--text-dim)', width: '10px' }}>{open ? '▼' : '▶'}</span>
        <span>📁</span>{name}
      </div>
      {open && (
        <div style={{ marginLeft: '20px' }}>
          {hasContent ? children : <p style={{ color: 'var(--text-disabled)', fontSize: '12px', margin: '4px 0' }}>No documents yet.</p>}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MyDocumentsPage — кабінет фрілансера, дві папки:
//   PERSONAL DOCUMENTS — все, що фрілансер завантажує при реєстрації/онбордингу
//                         (паспорт/фото/CV/інше) + підписана згода + інструкція.
//   PROJECTS — автопапка на кожен призначений проєкт. Наповнення документами
//              конкретного проєкту — окремий крок, поки що порожні папки.
// ─────────────────────────────────────────────────────────────────────────────
export default function MyDocumentsPage({ onBack }) {
  const [freelancer, setFreelancer] = useState(null);
  const [projects,   setProjects]   = useState([]);
  const [loading,     setLoading]   = useState(true);
  const [error,       setError]     = useState('');
  const [replacingId, setReplacingId] = useState(null);
  const [replaceError, setReplaceError] = useState('');

  // Окрема функція (не тільки useEffect), бо FolderNode викликає onRefresh
  // після кожної дії з папками/файлами (створення, перейменування, видалення,
  // завантаження) — потрібно перечитати саме projects, без флешу всієї сторінки.
  const refreshProjects = () => api.projects.mine().then(res => setProjects(res.projects || []));

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.freelancers.me(), api.projects.mine()])
      .then(([frRes, prRes]) => {
        if (cancelled) return;
        setFreelancer(frRes.freelancer);
        setProjects(prRes.projects || []);
      })
      .catch(err => { if (!cancelled) setError(err.message || 'Failed to load documents'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const url         = docId => api.freelancers.documentUrl(freelancer.id, docId);
  const downloadUrl = docId => api.freelancers.documentUrl(freelancer.id, docId, { download: true });

  // Заміна позначеного документа: вантажимо нову версію, бекенд сам видаляє
  // стару (файл+рядок) і знімає позначку — після успіху просто перечитуємо
  // профіль, щоб оновити id/назву файлу і прибрати банер "needs correction".
  const handleReplace = async (docId, file) => {
    setReplaceError('');
    setReplacingId(docId);
    try {
      await api.freelancers.replaceMyDocument(docId, file);
      const frRes = await api.freelancers.me();
      setFreelancer(frRes.freelancer);
    } catch (err) {
      setReplaceError(err.message || 'Failed to replace document');
    } finally {
      setReplacingId(null);
    }
  };

  const personalCount = freelancer
    ? freelancer.passportFiles.length + freelancer.photoFiles.length + freelancer.cvFiles.length
      + freelancer.otherDocs.length + freelancer.consentDocs.length + 1 // +1 гайд
    : 0;

  // п.9: папка проєкту в My Documents зʼявляється лише коли сам проєкт
  // (On Hold → Planning мапінг, як і скрізь) дійшов до Confirmed/Active/
  // Completed — до того фрілансер лише підтверджує готовність у My Projects,
  // документів ще нема. Бекенд і так повертає порожні projectDocuments/
  // myCrewFolder для таких проєктів — тут прибираємо сам рядок проєкту.
  const visibleProjects = projects.filter(p => {
    const status = p.status === 'On Hold' ? 'Planning' : p.status;
    return ['Confirmed', 'Active', 'Completed'].includes(status);
  });

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ background: 'var(--bg-card)', padding: '16px 32px', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid var(--border)' }}>
        <button onClick={onBack}
          style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-muted)', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
          ← Back
        </button>
        <span style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '18px', letterSpacing: '2px' }}>MY DOCUMENTS</span>
      </div>

      <div style={{ padding: '28px 32px', maxWidth: '760px', margin: '0 auto' }}>
        {error && (
          <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--accent)', color: 'var(--danger-text-soft)', padding: '10px 16px', borderRadius: '6px', fontSize: '13px', marginBottom: '20px' }}>
            ⚠️ {error}
          </div>
        )}

        {replaceError && (
          <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--accent)', color: 'var(--danger-text-soft)', padding: '10px 16px', borderRadius: '6px', fontSize: '13px', marginBottom: '20px' }}>
            ⚠️ {replaceError}
          </div>
        )}

        {!loading && freelancer && flaggedDocuments(freelancer).length > 0 && (
          <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--accent)', borderRadius: '8px', padding: '14px 18px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '20px' }}>🚩</span>
            <div>
              <div style={{ color: 'var(--danger-text-soft)', fontSize: '13px', fontWeight: 700 }}>
                {flaggedDocuments(freelancer).length} document{flaggedDocuments(freelancer).length === 1 ? '' : 's'} need{flaggedDocuments(freelancer).length === 1 ? 's' : ''} correction
              </div>
              <div style={{ color: 'var(--danger-text-soft)', fontSize: '12px', opacity: 0.85 }}>
                See the notes below on each document. You can't be assigned to projects until these are fixed — re-upload the corrected files in your Profile.
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <p style={{ color: 'var(--text-dim)', fontSize: '13px' }}>Loading…</p>
        ) : (
          <>
            <Folder icon="🗂️" name={`PERSONAL DOCUMENTS (${personalCount})`}>
              <FileRow href="/docs/Freelancer_Data_Entry_Guide.pdf" name="Guide to Completing Your Personal Data.pdf" hint="Reference" />

              {freelancer.consentDocs.map(d => (
                <FileRow key={`consent-${d.id}`} href={url(d.id)} downloadHref={downloadUrl(d.id)} name={d.name || 'Data Processing Consent (signed).pdf'} hint="Signed" flagReason={d.flagReason} />
              ))}
              {freelancer.passportFiles.map(d => (
                <FileRow key={`passport-${d.id}`} href={url(d.id)} downloadHref={downloadUrl(d.id)} name={d.name} hint="Passport" flagReason={d.flagReason}
                  docId={d.id} onReplace={handleReplace} replacing={replacingId === d.id} />
              ))}
              {freelancer.photoFiles.map(d => (
                <FileRow key={`photo-${d.id}`} href={url(d.id)} downloadHref={downloadUrl(d.id)} name={d.name} hint="Photo" flagReason={d.flagReason}
                  docId={d.id} onReplace={handleReplace} replacing={replacingId === d.id} />
              ))}
              {freelancer.cvFiles.map(d => (
                <FileRow key={`cv-${d.id}`} href={url(d.id)} downloadHref={downloadUrl(d.id)} name={d.name} hint="CV" flagReason={d.flagReason}
                  docId={d.id} onReplace={handleReplace} replacing={replacingId === d.id} />
              ))}
              {freelancer.otherDocs.map(doc => (doc.files || []).map(f => (
                <FileRow key={`other-${f.id}`} href={url(f.id)} downloadHref={downloadUrl(f.id)} name={f.name || doc.title} hint={doc.title} flagReason={f.flagReason}
                  docId={f.id} onReplace={handleReplace} replacing={replacingId === f.id} />
              )))}

              {personalCount === 1 && (
                <p style={{ color: 'var(--text-disabled)', fontSize: '12px', marginTop: '10px' }}>
                  Your uploaded documents (passport, photo, CV…) will appear here once added in your Profile.
                </p>
              )}
            </Folder>

            <Folder icon="📁" name={`PROJECTS (${visibleProjects.length})`}>
              {visibleProjects.length === 0 ? (
                <p style={{ color: 'var(--text-disabled)', fontSize: '12px', margin: '8px 0' }}>
                  {projects.length === 0
                    ? "You're not assigned to any projects yet."
                    : 'Project folders will appear here once a project is confirmed.'}
                </p>
              ) : (
                visibleProjects.map(p => {
                  const projectDocFolders = p.projectDocuments || [];
                  const hasProjectDocs = projectDocFolders.some(f => (f.files?.length || 0) > 0 || (f.subfolders?.length || 0) > 0);
                  const myFolder = p.myCrewFolder || { name: '', files: [], linkedDocuments: [] };
                  const myFiles = [...(myFolder.files || []), ...(myFolder.linkedDocuments || [])];
                  return (
                    <SubFolder key={p.id} name={p.name} hasContent={hasProjectDocs || myFiles.length > 0}>
                      {/* Керування папками (створити/перейменувати/видалити) —
                          через FolderNode, той самий компонент, що і в
                          детальній панелі проєкту (My Projects). */}
                      {projectDocFolders.map(f => (
                        <FolderNode key={f.id} folder={f} projectId={p.id} freelancerId={p.freelancerId}
                          area="project" isRoot myId={p.freelancerId} onRefresh={refreshProjects} />
                      ))}
                      <FolderNode folder={myFolder} projectId={p.id} freelancerId={p.freelancerId}
                        area="crew" isRoot myId={p.freelancerId} onRefresh={refreshProjects} />
                    </SubFolder>
                  );
                })
              )}
            </Folder>
          </>
        )}
      </div>
    </div>
  );
}
